import {
  Box,
  SimpleGrid,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import moment from "moment";
import { useEffect, useMemo } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { useOptimizeAccess } from "hooks/useOptimizeAccess";
import { OPTIMIZE_CARDS, OptimizeCardDef, OptimizeGame } from "./data";
import { useOptimizeStore } from "./store";
import { useOptimizeGames, useOptimizeLive } from "./liveStore";
import CardPreview from "./components/preview/CardPreview";
import GameStrip from "./components/GameStrip";
import OptimizeStatus, { useOptimizeReady } from "./components/OptimizeStatus";

const shuffle = <T,>(items: T[]): T[] => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const OptimizeCard: React.FC<{
  card: OptimizeCardDef;
  fallbackGame: OptimizeGame | null;
}> = ({ card, fallbackGame }) => {
  const history = useHistory();
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");

  const markedGameId = useOptimizeStore((state) => state.markedGameId);
  const states = useOptimizeLive((s) => s.states);
  const games = useOptimizeGames();

  // this card's enabled titles, keyed the way games are (`${platform}:${appId}`)
  const activeEntries = states
    .filter(
      (entry) => entry.optimization === card.id && entry.status === "enabled"
    )
    .map(
      (entry) =>
        [`${entry.platform}:${entry.appId}`, entry.activatedAt] as const
    );

  // marked game wins; otherwise a game already active on this card; otherwise random
  const markedGame = games.find((g) => g.id === markedGameId) ?? null;
  const activeGame = games.find((g) => g.id === activeEntries[0]?.[0]) ?? null;
  const previewGame = markedGame ?? activeGame ?? fallbackGame;

  // "Active since" for the marked game if set, else for any active title
  const relevantEntry = markedGameId
    ? activeEntries.find(([gameId]) => gameId === markedGameId)
    : activeEntries[0];
  const activeSince = relevantEntry?.[1];
  const relevantGame = games.find((g) => g.id === relevantEntry?.[0]);

  return (
    // no card chrome around the preview: the app card is the visual, and a
    // wrapper only stacked padding and a second background behind it
    <Box
      cursor="pointer"
      transition="all 0.2s ease"
      _hover={{ transform: "translateY(-4px)" }}
      onClick={() => history.push(`/optimize/${card.id}`)}
      display="flex"
      flexDirection="column"
    >
      <Text color={textColor} fontSize="md" fontWeight="bold" lineHeight="1.3">
        {card.title}
      </Text>
      <Box minH="18px" mb="6px">
        {activeSince ? (
          <Text color="green.500" fontSize="xs" fontWeight="600">
            Active since{" "}
            {moment.utc(activeSince).format("D MMM YYYY HH:mm:ss")} UTC
            {relevantGame ? ` for ${relevantGame.name}` : ""}
          </Text>
        ) : card.recommendedTag ? (
          <Text color="green.500" fontSize="xs" fontWeight="600">
            {card.recommendedTag}
          </Text>
        ) : (
          <Text color={subColor} fontSize="xs">
            Not active
          </Text>
        )}
      </Box>
      <Box flex="1">
        <CardPreview cardId={card.id} game={previewGame} />
      </Box>
    </Box>
  );
};

// A real grid, so cards on the same row share a height and previews stretch to
// fill their cell — except the top row's Recurring / Rewarded Ad cards, which
// keep their natural height (Günay's feedback: stretched next to the tall
// creative card they carried empty space). Rows breathe via rowGap plus a thin
// separator line drawn as a borderTop on every cell not in the first row.
const CardGrid: React.FC<{
  cards: OptimizeCardDef[];
  fallbackGames: Record<string, OptimizeGame>;
}> = ({ cards, fallbackGames }) => {
  const separator = useColorModeValue("gray.200", "whiteAlpha.200");
  return (
    <SimpleGrid
      columns={{ base: 1, md: 2, xl: 3 }}
      columnGap="20px"
      rowGap="28px"
      mb="40px"
      sx={{
        "& > *": {
          borderTop: "1px solid",
          borderTopColor: separator,
          pt: "24px",
        },
        // the first row carries no separator; how many cells that is depends
        // on the responsive column count
        "& > *:nth-of-type(1)": { borderTop: "none", pt: 0 },
        "& > *:nth-of-type(2)": {
          borderTop: { md: "none" },
          pt: { md: 0 },
        },
        "& > *:nth-of-type(3)": {
          borderTop: { xl: "none" },
          pt: { xl: 0 },
        },
      }}
    >
      {cards.map((card) => (
        <OptimizeCard
          key={card.id}
          card={card}
          fallbackGame={fallbackGames[card.id]}
        />
      ))}
    </SimpleGrid>
  );
};

export default function Optimize() {
  const { isLoaded, hasOptimizeAccess } = useOptimizeAccess();
  const initLive = useOptimizeLive((s) => s.init);
  const ready = useOptimizeReady();
  const games = useOptimizeGames();
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");

  useEffect(() => {
    initLive();
  }, [initLive]);

  // one random game per card, stable for the session (used when nothing is marked)
  const randomAssignment = useMemo(() => {
    const map: Record<string, OptimizeGame> = {};
    if (games.length === 0) return map;
    const pool = shuffle(games);
    OPTIMIZE_CARDS.forEach((card, index) => {
      map[card.id] = pool[index % pool.length];
    });
    return map;
  }, [games]);

  if (isLoaded && !hasOptimizeAccess) return <Redirect to="/ua" />;
  if (!isLoaded) return null;

  const integrations = OPTIMIZE_CARDS.filter(
    (card) => card.section === "integration"
  );
  const challenges = OPTIMIZE_CARDS.filter(
    (card) => card.section === "challenge"
  );

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Text color={textColor} fontSize="2xl" fontWeight="bold" mb="24px">
        Event Structure Optimization
      </Text>

      {!ready && <OptimizeStatus />}
      {!ready ? null : (
        <>
          <CardGrid cards={integrations} fallbackGames={randomAssignment} />

          <Text color={textColor} fontSize="xl" fontWeight="bold" mb="6px">
            Bonus Challenges
          </Text>
          <Text color={subColor} fontSize="sm" mb="16px" maxW="820px">
            You can have your game included in bonus challenges - special
            multi-game challenges that run across several titles. You can
            participate in a maximum of 3 challenges at the same time. New
            challenges can only be tested once every 2 weeks.
          </Text>

          <GameStrip />

          <CardGrid cards={challenges} fallbackGames={randomAssignment} />
        </>
      )}
    </Box>
  );
}
